import { loginSchema, type LoginInput } from '@cnss/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { TwoFactorStep } from './TwoFactorStep';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TwoFactorSetupPage } from '@/features/settings/TwoFactorSetupPage';
import { messageErreurApi } from '@/lib/api-error';
import { useAuth } from '@/lib/auth-context';

type PostPasswordStep = { kind: 'verify'; challengeToken: string } | { kind: 'setup'; challengeToken: string };

/**
 * Écran de connexion. Volontairement sobre : logo institutionnel, un seul
 * formulaire, aucun élément de marque superflu — cohérent avec un logiciel
 * d'État plutôt qu'un produit grand public.
 *
 * Après un mot de passe valide, deux étapes supplémentaires possibles selon la
 * réponse de l'API : vérification 2FA (compte déjà provisionné) ou
 * provisionnement 2FA forcé (rôle qui l'exige, pas encore active) — jamais les
 * deux, et jamais de session tant que l'une des deux n'est pas complétée.
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [postPasswordStep, setPostPasswordStep] = useState<PostPasswordStep | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (values: LoginInput) => {
    setServerError(null);
    try {
      const result = await login(values.username, values.password);
      if (result.requiresTwoFactor && result.challengeToken) {
        setPostPasswordStep({ kind: 'verify', challengeToken: result.challengeToken });
      } else if (result.requiresTwoFactorSetup && result.challengeToken) {
        setPostPasswordStep({ kind: 'setup', challengeToken: result.challengeToken });
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      // Message de l'API tel quel : compte verrouillé, compte suspendu,
      // serveur injoignable… Un texte générique enverrait l'agent chercher
      // une faute de frappe pour un problème qui n'en est pas une.
      setServerError(messageErreurApi(error, 'Identifiant ou mot de passe incorrect.'));
    }
  };

  if (postPasswordStep?.kind === 'verify') {
    return (
      <TwoFactorStep
        challengeToken={postPasswordStep.challengeToken}
        onSuccess={() => navigate('/', { replace: true })}
      />
    );
  }

  if (postPasswordStep?.kind === 'setup') {
    return (
      <TwoFactorSetupPage
        challengeToken={postPasswordStep.challengeToken}
        onSuccess={() => navigate('/', { replace: true })}
      />
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <ShieldCheck className="h-6 w-6" aria-hidden />
          </div>
          <CardTitle className="text-xl">Carte Sociale CNSS</CardTitle>
          <CardDescription>Accès réservé au personnel autorisé de la CNSS</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="username">Identifiant</Label>
              {/* Page dédiée à une seule action : le focus automatique sur le premier
                  champ est ici un gain d'accessibilité clavier, pas une gêne. */}
              {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
              <Input id="username" autoComplete="username" autoFocus {...register('username')} />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            {serverError && (
              <p
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {serverError}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Connexion en cours…' : 'Se connecter'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
